import type { OrderLineOptionDto } from '../../generated-api';
import type {
  AddComponentDto,
  AddItemDto,
  AddItemOptionDto,
  CartOrderLine,
  CartOrderLineComponent,
} from '../../types/cart';

export const getOrderLineOptionKey = (orderLineOption: OrderLineOptionDto) =>
  `${orderLineOption.itemVariantId}-${orderLineOption.price}-${orderLineOption.name}`;

/**
 * Helper function to generate key for add item options
 */
export const getUniqueAddItemOptionKey = (addItemOption: AddItemOptionDto) =>
  `${addItemOption.itemVariantId}-${addItemOption.price}-${addItemOption.name}`;

/**
 * Recursively generates a uniqueness key for a cart order line component.
 * Includes itemVariantId, price, unitQuantity, sorted options, comment, and nested components.
 */
export const getComponentKey = (component: CartOrderLineComponent): string => {
  const optionsKey = component.options
    .map((opt) => `${opt.itemVariantId}-${opt.price}-${opt.name}`)
    .sort()
    .join(',');

  const commentKey = component.commentId ? `-commentId:${component.commentId}` : '';
  const unitQuantityKey = component.unitQuantity ? `-uq:${component.unitQuantity}` : '';
  const courseKey = component.courseId ? `-course:${component.courseId}` : '';

  // Recursively get keys for nested components, sorted for consistency
  const nestedComponentsKey =
    component.components && component.components.length > 0
      ? `-components:[${component.components.map(getComponentKey).sort().join(',')}]`
      : '';

  return `${component.itemVariantId}-${component.price}${unitQuantityKey}-opts:[${optionsKey}]${commentKey}${courseKey}${nestedComponentsKey}`;
};

/**
 * Recursively generates a uniqueness key for an AddComponentDto.
 * Matches the structure of getComponentKey for CartOrderLineComponent.
 */
export const getAddComponentKey = (component: AddComponentDto): string => {
  const optionsKey = component.options.map(getUniqueAddItemOptionKey).sort().join(',');

  const commentKey = component.commentId ? `-commentId:${component.commentId}` : '';
  const unitQuantityKey = component.unitQuantity ? `-uq:${component.unitQuantity}` : '';
  const courseKey = component.courseId ? `-course:${component.courseId}` : '';

  // Recursively get keys for nested components, sorted for consistency
  const nestedComponentsKey =
    component.components && component.components.length > 0
      ? `-components:[${component.components.map(getAddComponentKey).sort().join(',')}]`
      : '';

  return `${component.itemVariantId}-${component.price}${unitQuantityKey}-opts:[${optionsKey}]${commentKey}${courseKey}${nestedComponentsKey}`;
};

export const getUniqueOrderLineKey = (orderLine: CartOrderLine) => {
  const optionsKey = orderLine.options.map(getOrderLineOptionKey).join('-');
  const courseIdKey = orderLine.courseId ? `-${orderLine.courseId}` : '';
  const commentIdKey = orderLine.commentId ? `-commentId:${orderLine.commentId}` : '';

  // Include nested components in the key, sorted for consistent matching
  const componentsKey =
    orderLine.components && orderLine.components.length > 0
      ? `-components:[${orderLine.components.map(getComponentKey).sort().join(',')}]`
      : '';

  return `${orderLine.itemVariantId}-${orderLine.price}-${orderLine.name}-${optionsKey}${courseIdKey}${commentIdKey}${componentsKey}`;
};

/** Build a grouping key for a component, excluding unitQuantity and commentId */
const getCollapseKey = (c: CartOrderLineComponent): string => {
  const optionsKey = c.options
    .map((o) => `${o.itemVariantId}-${o.price}-${o.name}-${o.quantity}`)
    .sort()
    .join(',');
  const commentKey = c.comment ? `-comment:${c.comment}` : '';
  const courseKey = c.courseId ? `-course:${c.courseId}` : '';
  const nestedKey =
    c.components && c.components.length > 0
      ? `-components:[${collapseComponents(c.components).map(getCollapseKey).sort().join(',')}]`
      : '';
  return `${c.itemVariantId}-${c.price}${courseKey}-opts:[${optionsKey}]${commentKey}${nestedKey}`;
};

/** Re-aggregate identical sibling components back into single entries with summed unitQuantity */
export const collapseComponents = (components: CartOrderLineComponent[]): CartOrderLineComponent[] => {
  const groups = new Map<string, CartOrderLineComponent>();
  for (const component of components) {
    const collapsed = component.components
      ? { ...component, components: collapseComponents(component.components) }
      : component;
    const key = getCollapseKey(collapsed);
    const existing = groups.get(key);
    if (existing) {
      groups.set(key, { ...existing, unitQuantity: (existing.unitQuantity ?? 1) + (collapsed.unitQuantity ?? 1) });
    }
    if (!existing) {
      groups.set(key, collapsed);
    }
  }
  return [...groups.values()];
};

export const getUniqueAddItemKey = (addItem: AddItemDto) => {
  const optionsKey = addItem.options.map(getUniqueAddItemOptionKey).join('-');
  const courseIdKey = addItem.courseId ? `-${addItem.courseId}` : '';
  const commentIdKey = addItem.commentId ? `-commentId:${addItem.commentId}` : '';

  // Include nested components in the key, sorted for consistent matching
  const componentsKey =
    addItem.components && addItem.components.length > 0
      ? `-components:[${addItem.components.map(getAddComponentKey).sort().join(',')}]`
      : '';

  return `${addItem.itemVariantId}-${addItem.price}-${addItem.name}-${optionsKey}${courseIdKey}${commentIdKey}${componentsKey}`;
};
